"use client";

import type React from "react";

import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Ban } from "lucide-react";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { CalendarTimeColumn } from "./CalendarTimeColumn";
import { CalendarDayHeaders } from "./CalendarDayHeaders";

import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";

interface CalendarGridBaseProps {
  isLoading: boolean;
  hours: number[];
  weekDays: Date[];
  shownDays: number;
  minHour: number;
  hourHeight: number;
  headerHeight: number;
  timeColumnWidth: number;
  children: React.ReactNode;
  isWeeklyTemplate?: boolean;
  canMakeReservations?: boolean;
}

export function CalendarGridBase({
  hours,
  weekDays,
  shownDays,
  minHour,
  hourHeight,
  headerHeight,
  timeColumnWidth,
  children,
  isWeeklyTemplate = false,
  canMakeReservations,
}: CalendarGridBaseProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation("workspaces");
  const { user } = useUserStore();
  const { data: workspaceInfo } = useWorkspaceInformationStore();

  const isBlocked = workspaceInfo?.is_workspace_blocked_for_user;
  const hasLeft = workspaceInfo?.has_user_left_workspace;
  const knowledgeCut = workspaceInfo?.knowledge_cut;

  const showDataDisclaimer = (isBlocked || hasLeft) && knowledgeCut;

  return (
    <div
      className="bg-background relative flex h-full flex-col"
      style={
        {
          "--calendar-hour-height": `${hourHeight}px`,
          "--calendar-header-height": `${headerHeight}px`,
          "--calendar-time-column-width": `${timeColumnWidth}px`,
        } as React.CSSProperties
      }
    >
      <div ref={scrollContainerRef} className="flex-1">
        <ScrollArea className="h-full max-h-[calc(100dvh_-_4rem)]" type={"auto"}>
          <div className={isWeeklyTemplate ? "min-w-[1400px]" : ""}>
            <div
              className="grid min-h-0"
              style={{
                gridTemplateColumns: `var(--calendar-time-column-width) repeat(${shownDays}, 1fr)`,
                gridTemplateRows: `var(--calendar-header-height) 1fr`,
                height: `calc(24 * var(--calendar-hour-height) + var(--calendar-header-height))`,
              }}
            >
              <div className="top-0 z-40 flex items-center" />
              <div
                className={`bg-background sticky top-0 z-50 col-span-full col-start-2 h-fit w-full overflow-hidden text-center`}
              >
                {showDataDisclaimer && (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex h-8 items-center gap-2 rounded-lg border-none bg-red-900/20 p-2 text-red-300">
                      {isBlocked ? <Ban className="h-3 w-3" /> : <Icon icon="mdi:alert" />}
                      <p className="flex items-center text-xs max-[475px]:flex-col max-[475px]:items-start min-[475px]:gap-1">
                        {isBlocked ? t("headerDisclaimer.blocked") : t("headerDisclaimer.left")}

                        {knowledgeCut && (
                          <span className="block text-xs opacity-80 max-[475px]:text-[9px]">
                            {t("headerDisclaimer.dataLimitCalendar", {
                              defaultValue:
                                "You can only see records added before {{knowledge_cut}}.",
                              knowledge_cut: format(new Date(knowledgeCut), "PPP", {
                                locale: getDateFnsLocale(i18n.language),
                              }),
                            })}
                          </span>
                        )}
                      </p>
                    </div>
                  </motion.div>
                )}
                {!canMakeReservations && user?.role === "student" && (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex h-8 items-center gap-2 rounded-lg border-none bg-red-900/20 p-2 text-red-300">
                      <Icon icon="mdi:calendar-remove" />
                      <p className="flex items-center text-xs max-[475px]:flex-col max-[475px]:items-start min-[475px]:gap-1">
                        {t("settings.studentReservations.disabledInformation", {
                          defaultValue:
                            "Reservations are currently disabled for this workspace. You cannot book new lessons at this time.",
                        })}
                      </p>
                    </div>
                  </motion.div>
                )}
                {/* Google Calendar reauth disclaimer - only for tutors and requires_reauth */}
                {user?.role === "tutor" &&
                  user?.specialRoleChecks?.google_calendar === "requires_reauth" && (
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      initial={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex h-8 items-center gap-2 rounded-lg border-none bg-amber-900/20 p-2 text-amber-300">
                        <Icon icon="mdi:sync-alert" />
                        <p className="flex items-center text-xs max-[475px]:flex-col max-[475px]:items-start min-[475px]:gap-1">
                          {t("settings.googleReauthNeeded", {
                            ns: "calendar",
                            defaultValue:
                              "Your Google Calendar connection needs reauthentication to work properly.",
                          })}
                        </p>
                      </div>
                    </motion.div>
                  )}
                <CalendarDayHeaders shownDays={shownDays} weekDays={weekDays} />
              </div>

              <CalendarTimeColumn hourHeight={hourHeight} hours={hours} minHour={minHour} />

              {children}
            </div>
          </div>
          {isWeeklyTemplate && (
            <ScrollBar className={"sticky bottom-2 z-[100]!"} orientation="horizontal" />
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
